import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { X, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { markAsRead } from '../store/notificationsSlice';

const NotificationBanner = ({ notification, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(true);
  const [showProgress, setShowProgress] = useState(true);

  useEffect(() => {
    // Play notification sound
    playNotificationSound();

    console.log('New notification received:', notification);
    
    // Show progress bar on new notification
    setShowProgress(true);
    
    // Auto-dismiss after 2 minutes (120 seconds)
    const timer = setTimeout(() => {
      handleClose();
    }, 120000);

    return () => clearTimeout(timer);
  }, [notification]);

  const playNotificationSound = () => {
    try {
      // Create audio context and play notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification sound (two-tone beep)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for exit animation
  };

  const handleClick = () => {
    // Mark notification as read before navigating
    if (!notification.is_read) {
      dispatch(markAsRead(notification.id));
    }
    
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.ticket_id) {
      navigate(`/tickets/${notification.ticket_id}`);
    }
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -100, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -100, scale: 0.8 }}
        transition={{ 
          type: "spring", 
          stiffness: 300, 
          damping: 25,
          duration: 0.4 
        }}
        className="max-w-md w-full"
      >
        <div 
          className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-3xl transition-shadow duration-200"
          onClick={handleClick}
        >
          {/* Progress bar for auto-dismiss - only show on new notifications */}
          {showProgress && (
            <motion.div
              key="progress-bar"
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 120, ease: "linear" }}
              style={{ height: "4px", backgroundColor: "#3b82f6", display: "block" }}
            />
          )}
          
          <div className="p-4 flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    {notification.title}
                  </h4>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {notification.message}
                  </p>
                  {notification.ticket_number && (
                    <span className="inline-block mt-2 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {notification.ticket_number}
                    </span>
                  )}
                </div>
                
                {/* Close button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Action hint */}
              <div className="mt-2 text-xs text-gray-400">
                Click to view • Auto-dismiss in 2 min
              </div>
            </div>
          </div>

          {/* Hover effect gradient */}
          <motion.div
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 0.05 }}
            className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500 pointer-events-none"
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationBanner;
